/**
 * components/auth/AntiBotFields.jsx
 *
 * Hook + componentes para integrar las 3 protecciones client-side del flujo auth:
 *   1. Honeypot field (oculto, accesible solo para bots)
 *   2. Form-loaded-at timestamp (anti-bots que envían en <1.5s)
 *   3. Cloudflare Turnstile widget (CAPTCHA)
 *
 * Uso:
 *   const { honeypot, setHoneypot, formLoadedAt, captchaToken, setCaptchaToken,
 *           captchaConfig, captchaError, resetCaptcha } = useAntiBot();
 *   ...
 *   <HoneypotField value={honeypot} onChange={setHoneypot} />
 *   <TurnstileWidget config={captchaConfig} onToken={setCaptchaToken} onError={...} />
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import { api } from "../../lib/apiClient";

// Carga el script de Turnstile globalmente (idempotente).
function loadTurnstileScript() {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.turnstile) return Promise.resolve();
  if (window.__turnstileLoading) return window.__turnstileLoading;
  window.__turnstileLoading = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = (e) => reject(e);
    document.head.appendChild(s);
  });
  return window.__turnstileLoading;
}

export function useAntiBot() {
  const [honeypot, setHoneypot] = useState("");
  const [formLoadedAt] = useState(() => Date.now());
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaConfig, setCaptchaConfig] = useState(null);
  const [captchaError, setCaptchaError] = useState("");

  useEffect(() => {
    let cancelled = false;
    api.get("/auth/captcha-config")
      .then(({ data }) => {
        if (!cancelled) setCaptchaConfig(data);
      })
      .catch(() => {
        if (!cancelled) setCaptchaConfig({ enabled: false });
      });
    return () => { cancelled = true; };
  }, []);

  const resetCaptcha = useCallback(() => {
    setCaptchaToken("");
    if (window.turnstile && window.__turnstileWidgetId) {
      try { window.turnstile.reset(window.__turnstileWidgetId); } catch (e) { /* noop */ }
    }
  }, []);

  return {
    honeypot, setHoneypot,
    formLoadedAt,
    captchaToken, setCaptchaToken,
    captchaConfig, captchaError, setCaptchaError,
    resetCaptcha,
  };
}

/**
 * Campo trampa: invisible para humanos, suele ser autofill por bots.
 * - tabIndex=-1: no es accesible por TAB
 * - autoComplete="new-password": evita autofill de gestores legítimos
 * - aria-hidden: lectores de pantalla lo ignoran
 * - posicionado fuera de pantalla
 */
export function HoneypotField({ value, onChange, name = "website" }) {
  return (
    <div
      aria-hidden="true"
      style={{
        position: "absolute", left: "-9999px", top: "-9999px",
        width: 1, height: 1, opacity: 0, pointerEvents: "none",
      }}
    >
      <label htmlFor={name}>{name}</label>
      <input
        id={name}
        name={name}
        type="text"
        tabIndex={-1}
        autoComplete="new-password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid="honeypot-field"
      />
    </div>
  );
}

/**
 * Turnstile widget. Solo se renderiza si config.enabled && site_key.
 * onToken: callback con el token resuelto.
 */
export function TurnstileWidget({ config, onToken, onError, action = "auth" }) {
  const containerRef = useRef(null);
  const [scriptReady, setScriptReady] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (!config?.enabled || !config?.site_key) return;
    loadTurnstileScript()
      .then(() => setScriptReady(true))
      .catch(() => setErrorMsg("No se pudo cargar el verificador anti-bot."));
  }, [config]);

  useEffect(() => {
    if (!scriptReady || !config?.enabled || !window.turnstile || !containerRef.current) return;
    try {
      const id = window.turnstile.render(containerRef.current, {
        sitekey: config.site_key,
        action,
        callback: (token) => {
          setErrorMsg("");
          onToken(token);
        },
        "error-callback": (e) => {
          setErrorMsg("Falló la verificación anti-bot. Recarga la página.");
          if (onError) onError(e);
        },
        "expired-callback": () => {
          onToken("");
        },
        theme: "light",
      });
      window.__turnstileWidgetId = id;
      return () => {
        try { window.turnstile.remove(id); } catch (e) { /* noop */ }
      };
    } catch (e) {
      setErrorMsg("No se pudo inicializar el verificador.");
    }
  }, [scriptReady, config, action, onToken, onError]);

  if (!config?.enabled) return null;

  return (
    <div className="my-2" data-testid="turnstile-widget">
      <div ref={containerRef} />
      {errorMsg && <p className="text-xs text-rose-600 mt-1">{errorMsg}</p>}
    </div>
  );
}
